import { endOfDay, startOfDay, subDays } from 'date-fns';
import { FC, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Pagination, PaginationParams } from '@bonusly/common-admin';
import {
	Box,
	Button,
	CheveronSmallDownOutline,
	CheveronSmallUpOutline,
	DocumentDownloadOutline,
	Flex,
	HStack,
	IconButton,
	Link,
	StyleProps,
	Table,
	Tbody,
	Td,
	Text,
	Tfoot,
	Th,
	Thead,
	Tr,
} from '@bonusly/design-system';
import { useBuildRecognizeURL } from '@bonusly/url';
import { useCurrentCompany } from '@bonusly/who-am-i';

import { useProfilePath } from '../../../profile/hooks/use-profile-path';
import { GetIncentivesSummaryParams, useGetIncentivesSummary } from '../hooks/use-incentives';
import { formatDisplayDateWithYear } from '../utils/date';
import { AttachmentButton } from './attachment-button';
import { SkeletonTableRows } from './skeleton-table-rows';
import { TableFilters } from './table-filters';

const today = new Date();
const thirtyDaysAgo = subDays(new Date(), 29);

export const defaultParams: GetIncentivesSummaryParams = {
	award_ids: [],
	end_date: endOfDay(today).toISOString(),
	page_number: 1,
	page_size: 24,
	sort_order: 'desc',
	start_date: startOfDay(thirtyDaysAgo).toISOString(),
};

export const IncentivesTable: FC = () => {
	const { t } = useTranslation('admin', {
		keyPrefix: 'incentives.summary',
	});
	const company = useCurrentCompany();
	const recognizeUrl = useBuildRecognizeURL();
	const { getProfileUrl } = useProfilePath();

	const [params, setParams] = useState<GetIncentivesSummaryParams>(defaultParams);

	const { data, isFetching } = useGetIncentivesSummary(params);

	const awards = data?.awards || [];
	const incentives = data?.claims;
	const pagination = data?.pagination;
	const hasPagination = Boolean(data?.pagination?.page_count && data.pagination.page_count > 1);

	const handleSortByDate = () => {
		const newSortOrder = params.sort_order === 'asc' ? 'desc' : 'asc';
		setParams({
			...params,
			// NOTE: reset to page 1 on sort
			page_number: 1,
			sort_order: newSortOrder,
		});
	};

	const handleDownload = useCallback(() => {
		const startDate = params.start_date.replaceAll(':', '%3A');
		const endDate = params.end_date.replaceAll(':', '%3A');

		let url = `${recognizeUrl('api/v2', 'admin/incentives/summary/export_csv')}?start_date=${startDate}&end_date=${endDate}`;

		if (params.award_ids) {
			params.award_ids.forEach((award_id) => {
				url += `&award_ids%5B%5D=${award_id}`;
			});
		}

		window.open(url, '_blank');
	}, [params.award_ids, params.end_date, params.start_date, recognizeUrl]);

	const setPaginationData = (newPaginationData: PaginationParams) => {
		setParams({
			...params,
			page_number: newPaginationData.page_number || 1,
		});
		window.scrollTo({ top: 0, behavior: 'smooth' });
	};

	return (
		<Box backgroundColor="bg.surface" borderRadius="lg" overflow="hidden">
			<HStack justifyContent="space-between">
				<TableFilters awards={awards} params={params} setParams={setParams} />
				<Button mr={4} onClick={handleDownload} data-testid="export-button">
					<DocumentDownloadOutline size="sm" mr={2} />
					<Text fontSize={100} fontWeight="medium">
						{t('csv_export')}
					</Text>
				</Button>
			</HStack>
			<Table variant="simple" layout="fixed">
				<Thead>
					<Tr backgroundColor="bg.soft" width="100%" _last={{ borderBottom: 'none' }}>
						<TableHeader w="13%">
							<Flex alignItems="center">
								{t('header.date')}
								<IconButton
									aria-label={t('header.sort_by_date')}
									data-testid="sort-button"
									onClick={handleSortByDate}
									_hover={{ bg: 'transparent' }}
									_active={{ bg: 'transparent' }}
								>
									{params.sort_order === 'desc' ? (
										<CheveronSmallDownOutline color="text.softer" size="sm" />
									) : (
										<CheveronSmallUpOutline color="text.softer" size="sm" />
									)}
								</IconButton>
							</Flex>
						</TableHeader>
						<TableHeader w="18%">{t('header.name')}</TableHeader>
						<TableHeader w="8%">{t('header.amount')}</TableHeader>
						<TableHeader w="15%">{t('header.recipient')}</TableHeader>
						<TableHeader w="22%">{t('header.reason')}</TableHeader>
						<TableHeader w="13%">{t('header.attachment')}</TableHeader>
						<TableHeader w="10%">{t('header.status')}</TableHeader>
					</Tr>
				</Thead>
				<Tbody>
					{isFetching ? (
						<SkeletonTableRows numberOfRows={24} />
					) : (
						incentives?.map((incentive, index) => {
							const displayDate = formatDisplayDateWithYear(incentive.decision_date);
							const amount = incentive.award.amount;
							const pointsName = amount === 1 ? company.budget.singular : company.budget.plural;
							const isLast = !hasPagination && index === incentives.length - 1;

							return (
								<Tr key={index}>
									<TableData data-testid="date-field" isLast={isLast}>
										{displayDate}
									</TableData>
									<TableData isLast={isLast} fontWeight="semibold">
										{incentive.award.name}
									</TableData>
									<TableData isLast={isLast}>
										{amount} {pointsName}
									</TableData>
									<TableData isLast={isLast}>
										<Link
											href={getProfileUrl(incentive.receiver[0].id)}
											target="_blank"
											color="primary.fg"
											fontWeight="medium"
											_hover={{
												color: 'primary.fgStrong',
												textDecoration: 'underline',
											}}
										>
											{incentive.receiver[0].display_name}
										</Link>
									</TableData>
									<TableData isLast={isLast}>{incentive.claim_reason || '-'}</TableData>
									<TableData isLast={isLast}>
										<AttachmentButton attachment={incentive.claim_attachment} />
									</TableData>
									<TableData isLast={isLast}>
										{t(`status.${incentive.state}`, {
											defaultValue: incentive.state,
										})}
									</TableData>
								</Tr>
							);
						})
					)}
					{!isFetching && !incentives?.length && (
						<Tr>
							<Td colSpan={7} textAlign="center" borderBottom="none">
								<Text color="text.softer">{t('no_data')}</Text>
							</Td>
						</Tr>
					)}
				</Tbody>
				{hasPagination && (
					<Tfoot>
						<Tr>
							<TableData colSpan={1} isLast>
								<Text color="text.softest" display="inline" fontSize={50} lineHeight={5} whiteSpace="nowrap">
									{t('pagination_count', {
										page_count: incentives?.length,
										total_count: pagination?.total_count,
									})}
								</Text>
							</TableData>
							<TableData colSpan={5} isLast>
								<Pagination
									data={{
										...pagination,
										enabled: true,
									}}
									setPaginationData={setPaginationData}
									fontSize={100}
								/>
							</TableData>
							<TableData colSpan={1} isLast />
						</Tr>
					</Tfoot>
				)}
			</Table>
		</Box>
	);
};

interface TableDataProps extends StyleProps {
	children?: React.ReactNode;
	colSpan?: number;
	isLast?: boolean;
}

export const TableData = ({ isLast, ...props }: TableDataProps) => {
	return <Td color="text.default" fontSize={100} fontWeight="normal" lineHeight={6} px={4} py={5} {...(isLast && { borderBottom: 'none' })} {...props} />;
};

const TableHeader = ({ ...props }) => {
	return <Th color="text.softer" fontSize={50} fontWeight="medium" lineHeight={5} px={4} py={2} textAlign="left" textTransform="capitalize" {...props} />;
};
